<template>
  <SlottedRow
    class="date-row-wrapper"
    :allow-delete="editing && displayField.allowDelete"
    :handle-delete="handleDelete"
    :uuid="displayField.uuid"
  >
    <template #label>
      <div
        v-tooltip="{
          text: label,
          disabled: !overflowing,
          openDelay: 800,
          location: 'bottom start',
        }"
      >
        <FieldSuggestions
          id="suggestions"
          ref="suggestions"
          :suggestions="suggestions"
          :current-selection="label"
          :editing="false"
          placeholder="Date"
          @update-selection="handleUpdateLabel"
          @update-overflowing="overflowing = true"
        />
      </div>
    </template>
    <template
      v-if="displayField.citation"
      #icon
    >
      <CitationIcon
        :display-field="displayField"
      />
    </template>
    <template #value>
      <div
        v-if="!editing || !displayField.allowEdit"
        class="date-row-value"
      >
        {{ value }}
      </div>
      <input
        v-else
        type="date"
        class="date-row-date-picker display-field-text"
        name="date-row-date-picker"
        :data-testid="`date-picker-${displayField.label}`"
        :value="value"
        @blur="handleDateChange"
        @mousedown.stop
      >
    </template>
  </SlottedRow>
</template>
<script>
import {DisplayField} from '@/lib/classes/InfoWindowContent';
import SlottedRow from './SlottedRow.vue';
import {mapState} from 'vuex';
import FieldSuggestions from './FieldSuggestions.vue';
import CitationIcon from '@/components/InfoWindow/DisplayFields/CitationIcon.vue';

export default {
  name: 'DateRow',
  components: {
    SlottedRow,
    FieldSuggestions,
    CitationIcon,
  },
  inject: ['contentType'],
  props: {
    displayField: {
      type: DisplayField,
      required: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['updateDisplayField', 'deleteDisplayField'],
  data() {
    return {
      /** @type {string} */
      displayLabel: '',
      /** @type {Date|string} */
      dateValue: this.displayField.value ?? null,
      /** @type {boolean} */
      overflowing: false,
    };
  },
  computed: {
    ...mapState('infowindow', ['default', 'fieldSuggestions']),
    label() {
      // if the label is blank or undefined, return a default
      return this.displayField.label || 'Date';
    },
    value() {
      // if the value is a date object, format it
      if (this.dateValue instanceof Date) {
        return this.formatDate(this.dateValue);
      }
      // otherwise, just return it as-is
      return this.dateValue;
    },
  },
  watch: {
    displayField: {
      handler(newVal) {
        this.displayLabel = newVal.label || 'Date';
        this.dateValue = this.normalizeDate(newVal.value);
      },
      deep: true,
      immediate: true,
    },
  },
  created() {
    this.suggestions = this.fieldSuggestions[this.contentType] ?? [];
  },
  methods: {
    /**
     * Attempt to convert a value into a date object
     * @param {Date|string} date
     * @return {Date|string}
     */
    normalizeDate(date) {
      // if it's already a date object, return a copy
      if (date instanceof Date) return new Date(date);

      // might be part of an ISO-8601 string like YYYY-MM-DD
      if (typeof date === 'string' && date.length === 10 && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Add time and user's current timezone to make it a valid ISO-8601 string
        const tzo = -(new Date().getTimezoneOffset());
        const h = Math.floor(Math.abs(tzo / 60));
        const m = Math.abs(tzo % 60);
        const isoTZO = `${tzo >= 0 ? '+' : '-'}${(h < 10 ? '0' : '')+h}:${(m < 10 ? '0' : '')+m}`;
        const d = new Date(`${date}T00:00:00${isoTZO}`);
        if (!isNaN(d)) return d;
      }
      // if it's not a date object or a recognizable string, just return it as-is
      return date;
    },
    handleUpdateLabel(newLabel) {
      this.label = newLabel;
      // Set focus to the text area for the row's value field
      this.setFocusToValue();
    },
    /**
     * Format a date object into a string
     * @param {Date} date
     * @return {string}
     */
    formatDate(date) {
      // return date in YYYY-MM-DD format
      return date.toISOString().slice(0, 10);
    },
    handleDateChange(e) {
      this.$emit('updateDisplayField', new DisplayField({
        ...this.displayField,
        value: e.target.value || null,
      }));
    },
    handleDelete() {
      this.$emit('deleteDisplayField', this.displayField);
    },
  },
};
</script>
<style lang="scss" scoped>
.date-row-wrapper {
  @extend .display-field-text;
}
.date-row-value {
  padding: 0 3px;
}
.date-row-date-picker {
  min-height: 23px;
  height: 23px;
  padding: 2px 5px;
  background: #EBEBEB 0% 0% no-repeat padding-box;
  border-color: #EBEBEB;
  border-radius: 2px;
  font-size: 13px;
  overflow-y:hidden;
  resize: none;
  width: 100%;
  text-align: left;
}

input:focus {
  outline-color: transparent;
}
</style>
